import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoScript from '@/components/base/SeoScript';
import { featuredComics } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = featuredComics.find((c) => c.slug === slug) || featuredComics[0];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug]);

  const chapters = Array.from({ length: Math.min(comic.latestChapter, 20) }, (_, i) => ({
    num: comic.latestChapter - i,
    title: `第${comic.latestChapter - i}话`,
    date: `2026-0${7 - Math.floor(i / 4)}-${String(30 - i).padStart(2, '0')}`,
  }));

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://zzxcdell.com/' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: `http://zzxcdell.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://zzxcdell.com/comic/${comic.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '韩漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://zzxcdell.com/comic/${comic.slug}`,
    },
  };

  useEffect(() => {
    document.title = `${comic.title} - 韩漫画网 | 无遮挡韩漫下拉式在线`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `${comic.title}：${comic.description}韩漫画网提供无遮挡下拉式免费在线阅读，加载迅速零干扰。`);
    }
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) {
      metaKw.setAttribute('content', `${comic.title},${comic.category},韩漫,韩漫画网,下拉式免费,无遮挡韩漫,在线漫画`);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `http://zzxcdell.com/comic/${comic.slug}`);
    }
  }, [comic]);

  return (
    <div className="min-h-screen bg-background-50">
      <SeoScript data={breadcrumbList} />
      <SeoScript data={articleSchema} />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 px-6 max-w-screen-xl mx-auto">
        <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs">
          <Link to="/" className="text-foreground-500 hover:text-foreground-200 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-600"></i>
          <Link to={`/category/${comic.categorySlug}`} className="text-foreground-500 hover:text-foreground-200 transition-colors">{comic.category}</Link>
          <i className="ri-arrow-right-s-line text-foreground-600"></i>
          <span className="text-foreground-300">{comic.title}</span>
        </nav>
      </div>

      {/* Hero / Detail Header */}
      <div className="relative w-full h-[420px] md:h-[600px] overflow-hidden mb-12">
        <img
          src={comic.coverImage}
          alt={comic.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-background-50/60 to-background-50" />

        <div className="absolute bottom-0 left-0 right-0 px-6 max-w-screen-xl mx-auto pb-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-7xl font-black text-foreground-50 leading-tight mb-4">
              {comic.title}
            </h1>
            <p className="text-foreground-300 text-sm md:text-base leading-relaxed max-w-xl mb-6">
              {comic.description}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <span className="text-foreground-100 font-semibold text-sm">{comic.rating}</span>
              </div>
              <span className="text-foreground-500 text-sm">{comic.views}次阅读</span>
              <span className="text-foreground-500 text-sm">作者：{comic.author}</span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-500/20 text-primary-400' : 'bg-secondary-500/20 text-secondary-400'}`}>
                {comic.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 max-w-screen-xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left: Chapter List */}
          <div className="lg:w-2/3">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-foreground-50">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-4 py-1.5 rounded-full border border-primary-500/40 text-primary-400 text-xs font-medium cursor-pointer whitespace-nowrap">正序</button>
                <button className="px-4 py-1.5 rounded-full text-foreground-500 text-xs font-medium hover:text-foreground-200 cursor-pointer whitespace-nowrap">倒序</button>
              </div>
            </div>

            <div className="space-y-2">
              {chapters.map((ch) => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.slug}/chapter/${ch.num}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-primary-400 font-medium whitespace-nowrap">第{ch.num}话</span>
                    <span className="text-sm text-foreground-200">{ch.title}</span>
                  </div>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.date}</span>
                </Link>
              ))}
            </div>

            {comic.latestChapter > 20 && (
              <div className="text-center mt-6">
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-300 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  查看更多章节
                  <i className="ri-arrow-down-s-line ml-1"></i>
                </button>
              </div>
            )}
          </div>

          {/* Right: Info Sidebar */}
          <div className="lg:w-1/3">
            <div className="bg-background-100 rounded-xl p-6 sticky top-24">
              <h3 className="font-heading text-lg font-bold text-foreground-50 mb-4">作品信息</h3>

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">作者</p>
                  <p className="text-sm text-foreground-200">{comic.author}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">类型</p>
                  <p className="text-sm text-foreground-200">{comic.category}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">状态</p>
                  <p className="text-sm text-foreground-200">{comic.status}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">评分</p>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm text-foreground-200 font-semibold">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">/ 10</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-2">标签</p>
                  <div className="flex flex-wrap gap-2">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-500/10 text-secondary-400 text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to={`/comic/${comic.slug}/chapter/1`}
                className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                从第一话开始阅读
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Related Comics */}
      <section className="py-16 md:py-24 px-6 max-w-screen-xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-foreground-50 mb-10">相关推荐</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {featuredComics.filter((c) => c.id !== comic.id).slice(0, 4).map((c) => (
            <Link
              key={c.id}
              to={`/comic/${c.slug}`}
              className="group relative rounded-lg overflow-hidden bg-background-100 cursor-pointer"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={c.coverImage}
                  alt={c.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="p-3">
                <h3 className="text-sm font-bold text-foreground-50 truncate">{c.title}</h3>
                <p className="text-xs text-foreground-500 mt-1">{c.rating}分 · {c.views}阅读</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}